import { useAuth, authorizedFetch } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export interface Activity {
  id: string
  type: 'exercise' | 'sleep' | 'wellness' // API returns "type" not "activity_type"
  date: string // ISO date
  notes?: string
  created_at: string
  
  // Exercise-specific
  exercise_type?: string
  
  // Sleep-specific
  sleep_start_time?: string // ISO datetime
  
  // Wellness-specific
  wellness_type?: string
  
  // Duration (shared across all types)
  duration?: string // ISO duration format (e.g., "PT30M")
}

export interface ActivityFilters {
  activity_type?: 'exercise' | 'sleep' | 'wellness'
  start_date?: string // ISO date
  end_date?: string // ISO date
  limit?: number
}

export interface CreateActivityData {
  activity_type: 'exercise' | 'sleep' | 'wellness'
  date?: string // ISO date
  notes?: string
  
  // Exercise-specific
  exercise_type_name?: string
  duration?: string // ISO duration format (e.g., "PT30M")
  
  // Sleep-specific
  sleep_start_time?: string // ISO datetime
  sleep_duration?: string // ISO duration format
  
  // Wellness-specific
  wellness_type_name?: string
  wellness_duration?: string // ISO duration format
}

export interface UpdateActivityData {
  activity_id: string
  date?: string
  notes?: string
  
  // Exercise-specific
  exercise_type_name?: string
  duration?: string
  
  // Sleep-specific
  sleep_start_time?: string
  sleep_duration?: string
  
  // Wellness-specific
  wellness_type_name?: string
  wellness_duration?: string
}

export function useActivities(filters: ActivityFilters = {}) {
  const { token } = useAuth()

  const queryKey = ['activities', filters]

  const activitiesQuery = useQuery({
    queryKey,
    queryFn: async (): Promise<Activity[]> => {
      if (!token) throw new Error('Not authenticated')

      const params = new URLSearchParams()
      if (filters.activity_type) params.append('activity_type', filters.activity_type)
      if (filters.start_date) params.append('start_date', filters.start_date)
      if (filters.end_date) params.append('end_date', filters.end_date)
      if (filters.limit) params.append('limit', filters.limit.toString())

      const url = `/api/activities/${params.toString() ? '?' + params.toString() : ''}`

      const response = await authorizedFetch(url)

      if (!response.ok) {
        throw new Error('Failed to fetch activities')
      }

      return response.json()
    },
    enabled: !!token,
    staleTime: 30 * 1000, // 30 seconds
  })

  return {
    activities: activitiesQuery.data || [],
    isLoading: activitiesQuery.isLoading,
    error: activitiesQuery.error,
    refetch: activitiesQuery.refetch,
  }
}

export function useActivity(activityId: string | null) {
  const { token } = useAuth()

  const activityQuery = useQuery({
    queryKey: ['activities', activityId],
    queryFn: async (): Promise<Activity> => {
      if (!token || !activityId) throw new Error('Not authenticated or no activity ID')

      const response = await authorizedFetch(`/api/activities/${activityId}`)

      if (!response.ok) {
        throw new Error('Failed to fetch activity')
      }

      return response.json()
    },
    enabled: !!token && !!activityId,
  })

  return {
    activity: activityQuery.data,
    isLoading: activityQuery.isLoading,
    error: activityQuery.error,
  }
}

export function useCreateActivity() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateActivityData): Promise<Activity> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch('/api/activities/', {
        method: 'POST',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || errorData.error || 'Failed to create activity')
      }

      return response.json()
    },
    onSuccess: () => {
      // Invalidate activities list cache
      queryClient.invalidateQueries({ queryKey: ['activities'] })
    },
  })
}

export function useUpdateActivity() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateActivityData): Promise<Activity> => {
      if (!token) throw new Error('Not authenticated')

      const { activity_id, ...updateData } = data

      const response = await authorizedFetch(`/api/activities/${activity_id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || errorData.error || 'Failed to update activity')
      }

      return response.json()
    },
    onSuccess: (data) => {
      // Invalidate activities list and specific activity cache
      queryClient.invalidateQueries({ queryKey: ['activities'] })
      queryClient.invalidateQueries({ queryKey: ['activities', data.id] })
    },
  })
}

export function useDeleteActivity() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (activityId: string): Promise<void> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`/api/activities/${activityId}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || errorData.error || 'Failed to delete activity')
      }
    },
    onSuccess: () => {
      // Invalidate activities list cache
      queryClient.invalidateQueries({ queryKey: ['activities'] })
    },
  })
}

